import {useCallback, useLayoutEffect, useRef} from 'react'

import type {TimelineItem} from './IssueTimeline'
import {
  decideTimelineFocus,
  LOAD_CONTROL_SELECTOR,
  loadControlSelector,
  resolveBaseItemFocusTarget,
  resolveTimelineFocusTarget,
} from './timeline-focus-target'

type PendingFocus = {
  baseId: string
  isComment: boolean
  position: 'top' | 'bottom'
  isLoadAll: boolean
  itemCountAtRequest: number
}

/**
 * Manages keyboard focus after a timeline "Load more"/"Load all" control is activated.
 *
 * `requestFocusAfterLoad` is called synchronously when the load is triggered; a
 * layout effect then runs after every timeline commit, reads the DOM, and defers to
 * `decideTimelineFocus` (a pure, unit-tested matrix) for what to do — a deterministic
 * post-commit signal, so no polling or timeouts are needed. In every case focus
 * moves to the newly-loaded content (its heading), falling back to the base item so
 * focus is never stranded on `<body>`; the matrix only decides *when* it is safe to
 * do so (the control unmounting for "Load all", the rows committing for a partial
 * "Load more"). A sibling control (top vs bottom, when deep-linked to a comment) can
 * coexist, so the control lookup is scoped to the activated position.
 */
export function useFocusAfterTimelineLoad(
  timelineItems: TimelineItem[],
): (baseId: string, isComment: boolean, position: 'top' | 'bottom', isLoadAll: boolean) => void {
  const pendingRef = useRef<PendingFocus | null>(null)
  // Last committed item count, read at request time so the partial-load branch can
  // tell when new rows have landed (and the page has remounted the control).
  const itemCountRef = useRef(timelineItems.length)

  const requestFocusAfterLoad = useCallback(
    (baseId: string, isComment: boolean, position: 'top' | 'bottom', isLoadAll: boolean) => {
      pendingRef.current = {baseId, isComment, position, isLoadAll, itemCountAtRequest: itemCountRef.current}
    },
    [],
  )

  useLayoutEffect(() => {
    const itemCountAtCommit = timelineItems.length
    itemCountRef.current = itemCountAtCommit

    const pending = pendingRef.current
    if (!pending) return

    const active = document.activeElement
    const control = document.querySelector<HTMLElement>(loadControlSelector(pending.position))

    const decision = decideTimelineFocus({
      isLoadAll: pending.isLoadAll,
      controlPresent: control !== null,
      itemsGrew: itemCountAtCommit > pending.itemCountAtRequest,
      focusEscaped: active !== null && active !== document.body && !active.closest(LOAD_CONTROL_SELECTOR),
    })

    if (decision === 'wait') return

    if (decision === 'focus-content') {
      const target =
        resolveTimelineFocusTarget(pending.baseId, pending.isComment) ?? resolveBaseItemFocusTarget(pending.baseId)
      target?.focus({preventScroll: true})
    }
    // 'abort' and 'focus-content' both consume the request.
    pendingRef.current = null
  }, [timelineItems])

  return requestFocusAfterLoad
}
